import React, { useState, useEffect } from 'react';
import FileService from '../services/FileService';
import '../assets/styles.css';
import './FlowSelector.css';

const FlowSelector = ({ onClose, onSelectFlow, isModal = false }) => {
  const [availableFlows, setAvailableFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);

  // Focus on search input when component mounts
  useEffect(() => {
    document.title = "Select Flow - astTECS Flow Builder";
    const searchInput = document.getElementById('flow-search');
    if (searchInput) {
      searchInput.focus();
    }
    fetchFlows();
  }, []);

  const fetchFlows = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all JSON flows from the /var/www/html/voicebot-flow/flowDB directory
      console.log('Fetching flows from flowDB directory...');
      const flowsList = await FileService.getFlowsDB();
      console.log('Received flows:', flowsList);
      setAvailableFlows(flowsList);
    } catch (error) {
      console.error("Error fetching flows:", error);
      // Show helpful error message
      if (error.message.includes('Network error')) {
        setError(`Could not connect to server. Please make sure the server is running with: \n                 \"node backend/server.js\" and refresh the page.`);
      } else {
        setError(`Failed to load flows: ${error.message}`);
      }
      // Show mock flows for development or if error occurs
      if (process.env.NODE_ENV === 'development') {
        console.log('Loading mock flows for development');
        setAvailableFlows([
          { name: 'Debt Collection', filename: 'debt_collection.json' },
          { name: 'Technical Support', filename: 'technical_support.json' },
          { name: 'Product Review', filename: 'product_review.json' }
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFlow = (flow) => {
    setSelectedFlow(flow);
    // Don't automatically load the flow, just select it
  };

  const handleLoadFlow = async () => {
    if (selectedFlow) {
      try {
        // Load the flow from flowDB (GUI format)
        const flowData = await FileService.getFlowDB(selectedFlow.filename);
        if (isModal && onSelectFlow) {
          onSelectFlow({ ...selectedFlow, flowData });
        } else if (window.opener && !window.opener.closed) {
          window.opener.location.href = `/builder/${selectedFlow.filename}`;
          window.close();
        } else {
          console.error('No valid flow loading method available');
          alert('Cannot load the selected flow. Please try again.');
        }
      } catch (error) {
        alert(`Failed to load flow: ${error.message}`);
      }
    }
  };

  const handleDoubleClick = (flow) => {
    setSelectedFlow(flow);
    handleLoadFlow();
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const handleRefresh = () => {
    fetchFlows();
  };

  // Filter flows based on search term
  const filteredFlows = availableFlows.filter(flow => 
    flow.name.toLowerCase().includes(searchTerm) || 
    flow.filename.toLowerCase().includes(searchTerm)
  );

  const handleClose = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      window.close();
    }
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog">
        <div className="dialog-header">
          <h2>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="9" y1="15" x2="15" y2="15"></line>
            </svg>
            Select Flow
          </h2>
          <button 
            onClick={handleClose} 
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem', color: '#6a1b9a', fontWeight: 'bold' }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        
        <div style={{ padding: '0 1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <div style={{ position: 'relative', flex: '1', marginRight: '1rem' }}>
              <input
                id="flow-search"
                type="text"
                className="form-control"
                placeholder="Search flows by name or filename..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ paddingLeft: '30px', width: '100%' }}
              />
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#888' }}>
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </div>
            <button className="btn btn-secondary" onClick={handleRefresh}>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px' }}>
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
              </svg>
              Refresh
            </button>
          </div>
        </div>

        <div style={{ padding: '0 1rem 1rem' }}>
          <p style={{ margin: '0', fontSize: '0.9rem', color: '#666', backgroundColor: '#f8f9fa', padding: '0.5rem', borderRadius: '4px' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
            Loading flows from <strong>/var/www/html/voicebot-flow/flowDB</strong> directory for canvas editing
          </p>
        </div>

        <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '0 1rem' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ display: 'inline-block', width: '30px', height: '30px', border: '3px solid #f3e5f5', borderRadius: '50%', borderTopColor: '#6a1b9a', animation: 'spin 1s linear infinite' }}></div>
              <p>Loading flows from server directory...</p>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
              <p>{error}</p>
              <button 
                className="btn btn-secondary"
                onClick={handleRefresh}
                style={{ marginTop: '1rem' }}
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {filteredFlows.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '12px', opacity: 0.5 }}>
                    <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z"></path>
                    <path d="M13 2v7h7M9.8 16l-1.7 1.7M9.8 12.3l-1.7-1.7M14 11.8L16 14M14 16l2-2.2"></path>
                  </svg>
                  <p>No flows found in /var/www/html/voicebot-flow/flowDB directory.</p>
                  {searchTerm && <p>Try adjusting your search term or refresh the list.</p>}
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', fontWeight: 'bold', borderBottom: '2px solid #eee', padding: '0.5rem 0', marginBottom: '0.5rem' }}>
                    <div style={{ flex: '2' }}>Flow Name</div>
                    <div style={{ flex: '1' }}>File</div>
                  </div>
                  <div>
                    {filteredFlows.map((flow, index) => (
                      <div
                        key={index}
                        className={selectedFlow && selectedFlow.filename === flow.filename ? 'intent-item active' : 'intent-item'}
                        onClick={() => handleSelectFlow(flow)}
                        onDoubleClick={() => handleDoubleClick(flow)}
                        style={{ 
                          cursor: 'pointer', 
                          padding: '0.75rem',
                          marginBottom: '0.5rem',
                          borderRadius: '4px',
                          display: 'flex'
                        }}
                      >
                        <div style={{ flex: '2', fontWeight: '500' }}>{flow.name}</div>
                        <div style={{ flex: '1', fontSize: '0.9rem', color: '#666' }}>{flow.filename}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="dialog-actions">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {selectedFlow ? (
              <div>
                <span style={{ marginRight: '0.5rem' }}>Selected:</span>
                <strong>{selectedFlow.name}</strong>
                <small style={{ display: 'block', fontSize: '0.8rem', color: '#666' }}>{selectedFlow.filename}</small>
              </div>
            ) : (
              <span>No flow selected</span>
            )}
          </div>
          <div>
            <button className="btn btn-secondary" onClick={handleClose} style={{ marginRight: '0.5rem' }}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={handleLoadFlow}
              disabled={!selectedFlow}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="7 10 12 15 17 10"></polyline>
                <line x1="12" y1="15" x2="12" y2="3"></line>
              </svg>
              Load Flow
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlowSelector; 

